<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import {
  ATTRIBUTE_MODELS,
  ATTRIBUTE_TYPES,
  EVENT_ATTRIBUTE_TYPES,
  ATTRIBUTE_CATEGORIES,
} from './constants';

import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

export default {
  components: {
    NextButton,
    TagInput,
  },
  props: {
    // Matches ATTRIBUTE_MODELS ids and the Conversation/Contact/Event tab keys.
    selectedAttributeModelTab: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeModel: this.selectedAttributeModelTab ?? 0,
      attributeType: 0,
      attributeKey: '',
      attributeCategory: 5,
      customAttributeCategoryId: null,
      selectedCategoryOption: 'parent_5',
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      values: [],
      tagInputTouched: false,
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
      customAttributeCategories: 'customAttributeCategories/getRecords',
    }),
    models() {
      return ATTRIBUTE_MODELS.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_MODELS.${item.key}`),
      }));
    },
    types() {
      const items = this.isEventAttribute
        ? [...ATTRIBUTE_TYPES, ...EVENT_ATTRIBUTE_TYPES]
        : ATTRIBUTE_TYPES;
      return items.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    categories() {
      return ATTRIBUTE_CATEGORIES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.${item.key}`),
      }));
    },
    combinedCategoryOptions() {
      const options = [];
      this.categories.forEach(cat => {
        options.push({
          value: `parent_${cat.id}`,
          label: cat.option,
          isParent: true,
        });
        const subCats = (this.customAttributeCategories || []).filter(
          c => c.parent_category === cat.key.toLowerCase() && c.active
        );
        subCats.forEach(sub => {
          options.push({
            value: `sub_${sub.id}`,
            label: `${cat.option} / ${sub.name}`,
            isParent: false,
          });
        });
      });
      return options;
    },
    isEventAttribute() {
      return this.attributeModel === 2;
    },
    isTagInputEmpty() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.tagInputTouched && this.isTagInputEmpty;
    },
    attributeListValues() {
      return this.values;
    },
    isButtonDisabled() {
      return (
        this.v$.displayName.$invalid ||
        this.v$.description.$invalid ||
        this.uiFlags.isCreating ||
        this.isTagInputEmpty
      );
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
  },

  validations: {
    displayName: {
      required,
      minLength: minLength(1),
    },
    description: {
      required,
    },
    attributeModel: {
      required,
    },
    attributeType: {
      required,
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },

  watch: {
    attributeType() {
      this.tagInputTouched = false;
      this.values = [];
    },
    selectedCategoryOption(val) {
      if (val && val.startsWith('sub_')) {
        const subId = Number(val.replace('sub_', ''));
        const subCat = (this.customAttributeCategories || []).find(
          c => c.id === subId
        );
        if (subCat) {
          const parent = this.categories.find(
            c => c.key.toLowerCase() === subCat.parent_category
          );
          this.attributeCategory = parent ? parent.id : 5;
          this.customAttributeCategoryId = subId;
        }
      } else if (val && val.startsWith('parent_')) {
        this.attributeCategory = Number(val.replace('parent_', ''));
        this.customAttributeCategoryId = null;
      }
      if (this.displayName) {
        this.attributeKey = this.generateAttributeKey(this.displayName);
      }
    },
  },

  mounted() {
    this.$store.dispatch('customAttributeCategories/get');
  },

  methods: {
    onDisplayNameChange() {
      this.attributeKey = this.generateAttributeKey(this.displayName);
    },
    generateAttributeKey(name) {
      const slug = convertToAttributeSlug(name);
      if (!this.isEventAttribute || !slug) return slug;
      const category = this.categories.find(
        c => c.id === this.attributeCategory
      );
      const prefix = category ? category.key.toLowerCase() : 'other';
      return `${prefix}_${slug}`;
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
    resetForm() {
      this.displayName = '';
      this.description = '';
      this.attributeModel = this.selectedAttributeModelTab ?? 0;
      this.attributeType = 0;
      this.attributeKey = '';
      this.attributeCategory = 5;
      this.customAttributeCategoryId = null;
      this.selectedCategoryOption = 'parent_5';
      this.regexPattern = null;
      this.regexCue = null;
      this.regexEnabled = false;
      this.values = [];
      this.tagInputTouched = false;
      this.v$?.$reset?.();
    },
    onCancel() {
      this.resetForm();
      this.$emit('close');
    },
    async addAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          attribute_model: this.attributeModel,
          attribute_display_type: this.attributeType,
          attribute_key: this.attributeKey,
          attribute_category: this.isEventAttribute
            ? this.attributeCategory
            : null,
          custom_attribute_category_id: this.isEventAttribute
            ? this.customAttributeCategoryId
            : null,
          attribute_values: this.attributeListValues,
          regex_pattern: this.regexPattern
            ? new RegExp(this.regexPattern).toString()
            : null,
          regex_cue: this.regexCue,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.ADD.API.SUCCESS_MESSAGE');
        this.resetForm();
        this.$emit('close');
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="$t('ATTRIBUTES_MGMT.ADD.TITLE')" />

    <form class="flex w-full" @submit.prevent="addAttributes">
      <div class="w-full">
        <label :class="{ error: v$.attributeModel.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
          <select v-model="attributeModel">
            <option v-for="model in models" :key="model.id" :value="model.id">
              {{ model.option }}
            </option>
          </select>
          <span v-if="v$.attributeModel.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.ERROR') }}
          </span>
        </label>
        <label v-if="isEventAttribute">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CATEGORY.LABEL') }}
          <select v-model="selectedCategoryOption">
            <option value="" disabled>
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CATEGORY.PLACEHOLDER') }}
            </option>
            <option
              v-for="opt in combinedCategoryOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>
        <woot-input
          v-model="displayName"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: v$.displayName.$error }"
          :error="
            v$.displayName.$error
              ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
          @update:model-value="onDisplayNameChange"
          @blur="v$.displayName.$touch"
        />
        <woot-input
          v-model="attributeKey"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
          type="text"
          :class="{ error: v$.attributeKey.$error }"
          :error="v$.attributeKey.$error ? keyErrorMessage : ''"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
          @blur="v$.attributeKey.$touch"
        />
        <label :class="{ error: v$.description.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
          <textarea
            v-model="description"
            rows="3"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
          <span v-if="v$.description.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
          </span>
        </label>
        <label :class="{ error: v$.attributeType.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
          <select v-model="attributeType">
            <option v-for="type in types" :key="type.id" :value="type.id">
              {{ type.option }}
            </option>
          </select>
          <span v-if="v$.attributeType.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
          </span>
        </label>
        <div v-if="isAttributeTypeList" class="mb-4">
          <label class="mb-1 block">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.LABEL') }}
          </label>
          <div
            class="rounded-xl border px-3 py-2"
            :class="isTagInputInvalid ? 'border-n-ruby-9' : 'border-n-weak'"
          >
            <TagInput
              v-model="values"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
              "
              allow-create
              @blur="tagInputTouched = true"
            />
          </div>
          <label
            v-show="isTagInputInvalid"
            class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
          </label>
        </div>
        <div v-if="isAttributeTypeText">
          <input
            v-model="regexEnabled"
            type="checkbox"
            @input="toggleRegexEnabled"
          />
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
        </div>
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexPattern"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
          type="text"
          :placeholder="
            $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
          "
        />
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexCue"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
        />
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onCancel"
          />
          <NextButton
            type="submit"
            :label="$t('ATTRIBUTES_MGMT.ADD.SUBMIT')"
            :disabled="isButtonDisabled"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.key-value {
  padding: 0 0.5rem 0.5rem 0;
  font-family: monospace;
}
</style>
